import React from "react";
import {Container} from "react-bootstrap";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";
import NutzungSparkline from "../../Components/Nutzung/NutzungSparkline.jsx";
import HelpTooltip from "../../Components/HelpTooltip.jsx";
import {bereichFarbe, bereichLabel, formatDuration} from "../../Components/Nutzung/format.js";
import {formatNumber, formatPercent} from "../../Components/Statistics/format.js";
import {BereichLink} from "../../Components/Nutzung/Links.jsx";

/**
 * Eine einzelne Kachel des Bereichsraster. Nutzer/Ereignisse/Anteil kommen
 * aus der EINEN gemeinsamen Antwort von /nutzung/daten/bereiche (bestehender
 * Endpunkt, unverändert - Auftrag Teil 1). Aktive Zeit und die kleine
 * Verlaufslinie holt sich jede Kachel zusätzlich SELBST über den ebenfalls
 * bestehenden Endpunkt /nutzung/daten/verlauf, gefiltert auf genau diesen
 * Bereich - das ist bewusst kein neuer Endpunkt, sondern derselbe, den
 * NutzungVerlaufChart im Überblick schon für die Gesamtsumme nutzt, hier nur
 * je Bereich aufgerufen. Die feste Bereichsliste hat nur 12 Einträge, das
 * ist kein Mitarbeiter-Loop im Sinn der Leistungsvorgabe (Spezifikation
 * Abschnitt 4), sondern ein fest begrenztes Kachelraster.
 */
function BereichKachel({bereich, filters}) {
    const verlauf = useStatisticsData("/nutzung/daten/verlauf", {...filters, bereich: bereich.bereich});
    const tage = Array.isArray(verlauf.data) ? verlauf.data : [];
    const aktivSekunden = tage.reduce((summe, tag) => summe + (Number(tag.aktive_sekunden) || 0), 0);
    const farbe = bereichFarbe(bereich.bereich);

    return (
        <div className="col-sm-6 col-lg-4 col-xl-3">
            <div className="card shadow-sm h-100 position-relative">
                <div className="card-body d-flex flex-column gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span
                            aria-hidden="true"
                            style={{width: 12, height: 12, borderRadius: 3, background: farbe, display: "inline-block", flexShrink: 0}}
                        />
                        <BereichLink bereich={bereich.bereich} className="fw-semibold text-body stretched-link fs-6" />
                    </div>

                    <div className="row g-2 small text-muted">
                        <div className="col-6">
                            <div className="fs-5 fw-semibold text-body">{formatNumber(bereich.nutzer)}</div>
                            Mitarbeiter
                        </div>
                        <div className="col-6">
                            <div className="fs-5 fw-semibold text-body">{formatNumber(bereich.ereignisse)}</div>
                            Ereignisse
                        </div>
                    </div>

                    <div className="small text-muted">
                        Anteil an allen Ereignissen: <strong className="text-body">{formatPercent((bereich.anteil || 0) * 100)}</strong>
                    </div>

                    {!verlauf.loading && aktivSekunden > 0 && (
                        <div className="small text-muted">
                            Aktive Zeit: <strong className="text-body">{formatDuration(aktivSekunden)}</strong>
                        </div>
                    )}

                    {!verlauf.loading && tage.length > 1 && (
                        <div className="mt-auto pt-1" style={{opacity: 0.9}}>
                            <NutzungSparkline values={tage.map((tag) => tag.aktive_sekunden)} width={140} height={30} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Übersicht aller Bereiche als Kachelraster (Auftrag Teil 1) - jede Kachel
 * führt über den stretched-link-Trick auf ihre Bereichsseite, der
 * Bereichsname selbst bleibt dabei EIN BereichLink (Kernanforderung 2 der
 * Spezifikation: durchgängige Verknüpfung über die gemeinsame Komponente).
 */
export default function NutzungBereiche() {
    const nutzungFilters = useNutzungFilter();
    const {filters} = nutzungFilters;
    const {data, loading, error} = useStatisticsData("/nutzung/daten/bereiche", filters);

    const rows = Array.isArray(data) ? [...data].sort((a, b) => (b.ereignisse || 0) - (a.ereignisse || 0)) : [];

    return (
        <Container fluid className="pt-2 pb-4">
            <NutzungLayout gruppe="Bereiche" titel="Bereiche – Übersicht" nutzungFilters={nutzungFilters}>
                <div className="d-flex align-items-center gap-2 mb-3 small text-muted">
                    Jede Kachel zeigt einen Bereich der Anwendung - Klick führt auf dessen Details.
                    <HelpTooltip placement="right" size={0.85}>
                        „Aktive Zeit" misst, wie dicht im Ordermanagement gearbeitet wird. Telefonate,
                        Arbeit in Weclapp oder im Lager sind darin nicht enthalten - die Zahl ist kein
                        Maß für Arbeitsleistung.
                    </HelpTooltip>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

                {!loading && !error && rows.length === 0 && (
                    <div className="text-muted">Keine Bereiche mit Aktivität im gewählten Zeitraum.</div>
                )}

                {!loading && !error && rows.length > 0 && (
                    <div className="row g-3">
                        {rows.map((bereich) => (
                            <BereichKachel key={bereich.bereich} bereich={bereich} filters={filters} />
                        ))}
                    </div>
                )}
            </NutzungLayout>
        </Container>
    );
}
